import React, { useMemo } from 'react';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Form, Formik } from 'formik';
import { useSelector } from 'react-redux';
import { IntlShape, useIntl } from 'react-intl';

import TextField from '@/components/form/text/TextField';
import Tooltip from '@/components/Tooltip';
import { useBankIdProfile } from '@/hooks/useBankIdProfile';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { doNothing } from '@/utils/utils';
import { User } from '@/models/user.model';
import { selectIsFastRegThDone } from '@/store/selectors/userSelectors';

type FieldConfig = {
  id: string;
  value: string;
  labelIntlId: string;
  isDisplayed: boolean;
  fieldClassName?: string;
};

type FieldsConfigs = Array<FieldConfig>;

const getUserBirthdateMeta = (user: User) => {
  const birthDateValues = user.BirthDate.toString().split('-');
  const birthYear = birthDateValues[0];
  const birthMonth = birthDateValues[1];
  const birthDay = birthDateValues[2].split('T')[0];

  return { birthYear, birthMonth, birthDay };
};

const getFieldsConfigs = (
  user: User,
  isFastRegThDone: boolean,
  isCompletedBankIdProfile: boolean,
): FieldsConfigs => {
  const { birthYear, birthMonth, birthDay } = getUserBirthdateMeta(user);

  return [
    {
      id: 'fullName',
      value: `${user.FirstName} ${user.LastName}`,
      labelIntlId: 'inputs.fullName',
      isDisplayed: true,
    },
    {
      id: 'dateOfBirthYear',
      value: birthYear,
      labelIntlId: 'inputs.year',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'dateOfBirthMonth',
      value: birthMonth,
      labelIntlId: 'inputs.month',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'dateOfBirthDay',
      value: birthDay,
      labelIntlId: 'inputs.day',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'address',
      value: `${user.Address1}, ${user.City}`,
      labelIntlId: 'inputs.address',
      isDisplayed: !isFastRegThDone,
    },
    {
      id: 'country',
      value: user.CountryName,
      labelIntlId: 'inputs.country',
      isDisplayed: !isFastRegThDone,
      fieldClassName: 'sm:!col-span-2',
    },
    {
      id: 'postalCode',
      value: user.PostalCode,
      labelIntlId: 'inputs.postcode',
      isDisplayed: !isFastRegThDone,
      fieldClassName: 'sm:!col-span-1',
    },
    {
      id: 'mobile',
      value: `${user.MobilePrefix} ${user.Mobile}`,
      labelIntlId: 'inputs.number',
      isDisplayed: isCompletedBankIdProfile,
    },
    {
      id: 'email',
      value: user.Email,
      labelIntlId: 'inputs.email',
      isDisplayed: isCompletedBankIdProfile && !isFastRegThDone,
    },
  ];
};

const renderField = (config: FieldConfig, intl: IntlShape) => {
  return (
    <div className={config.fieldClassName} key={config.id}>
      <TextField
        disabled
        name={config.id}
        label={intl.formatMessage({ id: config.labelIntlId })}
        className="border-[1px] border-gray-300 font-bold"
        labelClassName="text-blue-blueMedium"
        endIcon={
          <Tooltip
            position="left"
            text={intl.formatMessage({
              id: 'inputs.locked.tooltip',
            })}
            className="max-w-[220px] sm:max-w-max"
          >
            <LockClosedIcon className="w-5 h-5 text-black" />
          </Tooltip>
        }
      />
    </div>
  );
};

const UserDetailsFields = ({ user }: { user: User }) => {
  const intl = useIntl();
  const { isCompletedBankIdProfile } = useBankIdProfile();
  const locale = useSelector(selectLocale);
  const isFastRegThDone = useSelector(selectIsFastRegThDone);

  const { birthYear, birthMonth, birthDay } = getUserBirthdateMeta(user);

  const initialValues = {
    fullName: `${user.FirstName} ${user.LastName}`,
    dateOfBirthYear: birthYear,
    dateOfBirthMonth: birthMonth,
    dateOfBirthDay: birthDay,
    address: `${user.Address1}, ${user.City}`,
    country: user.CountryName,
    postalCode: user.PostalCode,
    mobile: `${user.MobilePrefix} ${user.Mobile}`,
    email: user.Email,
  };

  const fields = useMemo(
    () =>
      getFieldsConfigs(user, isFastRegThDone, isCompletedBankIdProfile)
        .filter(field => field.isDisplayed)
        .map(field => renderField(field, intl)),
    [locale, user, isCompletedBankIdProfile],
  );

  return (
    <>
      <h2 className="w-full mt-0 text-[22px] mb-8">
        {intl.formatMessage({
          id: 'settings.details.personalData.header',
        })}
      </h2>
      <Formik initialValues={initialValues} onSubmit={doNothing} enableReinitialize>
        <Form className="w-full grid grid-cols-1 sm:grid-cols-3 gap-5 [&>div]:sm:col-span-3">
          {fields}
        </Form>
      </Formik>
    </>
  );
};

export default UserDetailsFields;
